import { useCallback, useEffect, useRef, useState } from 'react';
import { ENROLLMENT_SERVICE } from '../services/enrollment/enrollment-service';

const PENDING_COUNT_POLL_INTERVAL_MS = 60 * 1000;

/**
 * Polls only the pending-request count on an interval, via the dedicated
 * pending-requests-count endpoint — a bare COUNT query, not the full paginated inbox (Student
 * include, Course/Exam title lookups, signed payment-proof URLs). Mirrors useNotifications'
 * unread-count polling.
 */
export const useEnrollmentRequestsPendingCount = (enabled: boolean): number => {
    const [pendingCount, setPendingCount] = useState(0);
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    const refreshPendingCount = useCallback(async () => {
        if (!enabledRef.current) {
            return;
        }
        try {
            const response = await ENROLLMENT_SERVICE.getPendingEnrollmentRequestsCountAsync();
            setPendingCount(response.success ? response.data ?? 0 : 0);
        } catch {
            // Polling failures are silent — the badge just keeps its last known count.
        }
    }, []);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        void refreshPendingCount();
        const interval = setInterval(() => void refreshPendingCount(), PENDING_COUNT_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [enabled, refreshPendingCount]);

    return enabled ? pendingCount : 0;
};
